import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    UserPlus,
    Sparkles,
    Flame,
    CalendarCheck,
    Mail,
    ChevronRight,
    Loader2,
    ShieldAlert,
    DollarSign,
    TrendingUp,
    ArrowUpRight,
    ArrowDownRight,
    Minus,
    AlertCircle,
    Bell,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, PIPELINE_STAGES } from "@/lib/adminApi";
import SlaTimer from "@/components/admin/SlaTimer";
import { useAuth } from "@/lib/auth";

const GOLD = "#C9A84C";

const KPI_CARDS = [
    { key: "new_leads_24h", label: "New Leads · 24h", icon: UserPlus, tone: "sky" },
    { key: "new_leads_7d", label: "New Leads · 7d", icon: UserPlus, tone: "sky" },
    { key: "jarvis_qualified", label: "Jarvis Qualified", icon: Sparkles, tone: "indigo" },
    { key: "hot_leads_open", label: "Hot Leads (open)", icon: Flame, tone: "red" },
    { key: "booked", label: "Scheduled", icon: CalendarCheck, tone: "teal" },
    { key: "sent_to_brent_this_week", label: "Sent to Brent · 7d", icon: Mail, tone: "violet" },
    { key: "jarvis_compliance_violations_7d", label: "Jarvis Compliance · 7d", icon: ShieldAlert, tone: "amber" },
];

const TONE_CLASSES = {
    sky: "from-sky-500 to-blue-600",
    indigo: "from-indigo-500 to-mm-navy",
    red: "from-rose-500 to-red-600",
    teal: "from-teal-500 to-emerald-600",
    violet: "from-violet-500 to-indigo-600",
    amber: "from-amber-500 to-orange-600",
};

export default function AdminDashboard() {
    const { user } = useAuth();
    const [kpis, setKpis] = useState(null);
    const [pipe, setPipe] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [closedDeals, setClosedDeals] = useState([]);
    const [paymentWatch, setPaymentWatch] = useState(null);

    const load = async () => {
        try {
            const [k, p, a, cd, pw] = await Promise.all([
                adminApi.kpis(),
                adminApi.pipeline(),
                adminApi.hotAlerts({ status: "open", limit: 10 }),
                adminApi.closedDealsRecent(10).catch(() => ({ items: [] })),
                adminApi.paymentWatchSummary().catch(() => null),
            ]);
            setKpis(k);
            setPipe(p);
            setAlerts(a.items || []);
            setClosedDeals(cd.items || []);
            setPaymentWatch(pw);
        } catch {
            toast.error("Couldn't load dashboard data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 20000);
        return () => clearInterval(t);
         
    }, []);

    const handleAck = async (id) => {
        try {
            await adminApi.ackAlert(id);
            setAlerts((prev) =>
                prev.map((a) => (a.id === id ? { ...a, acknowledged: true, acknowledged_at: new Date().toISOString() } : a)),
            );
            toast.success("Alert acknowledged.");
        } catch (err) {
            toast.error("Couldn't acknowledge.");
        }
    };

    return (
        <div className="space-y-8" data-testid="admin-dashboard">
            <div>
                <div className="mm-eyebrow">Dashboard</div>
                <h1 className="mm-display text-3xl text-mm-navy sm:text-4xl">
                    Welcome back{user?.name ? `, ${user.name}` : ""}.
                </h1>
                <p className="mt-1.5 text-sm text-mm-muted">
                    Nothing advances unless you click it. Every handoff is explicit.
                </p>
            </div>

            {/* KPIs */}
            <section data-testid="kpi-grid">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    {KPI_CARDS.map((c, i) => {
                        const Icon = c.icon;
                        const value = kpis?.[c.key];
                        return (
                            <motion.div
                                key={c.key}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.04 }}
                                className="mm-card relative overflow-hidden p-4"
                                data-testid={`kpi-${c.key}`}
                            >
                                <div
                                    aria-hidden
                                    className={`absolute -right-5 -top-5 h-16 w-16 rounded-full bg-gradient-to-br ${TONE_CLASSES[c.tone]} opacity-15`}
                                />
                                <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-mm-muted">
                                    <Icon size={12} />
                                    {c.label}
                                </div>
                                <div className="mt-2 mm-display text-3xl font-semibold text-mm-navy">
                                    {loading ? "—" : value ?? 0}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Phase 5 — Gold KPI row: policies closed + premium volume */}
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <GoldKpiCard
                        label="Policies Closed · Month"
                        icon={DollarSign}
                        value={kpis?.policies_closed_month}
                        prev={kpis?.policies_closed_last_month}
                        loading={loading}
                        testId="kpi-policies-closed-month"
                    />
                    <GoldKpiCard
                        label="Premium Volume · Month"
                        icon={TrendingUp}
                        value={centsToMoney(kpis?.premium_volume_month_cents)}
                        subValue={kpis?.premium_volume_month_cents
                            ? `(${centsToMoney((kpis?.premium_volume_month_cents || 0) * 12)}/yr)`
                            : null}
                        prev={kpis?.premium_volume_last_month_cents}
                        current={kpis?.premium_volume_month_cents}
                        loading={loading}
                        testId="kpi-premium-volume-month"
                    />
                </div>
            </section>

            {/* Phase 5.5 — Payment Watch */}
            {paymentWatch && <PaymentWatchCard summary={paymentWatch} loading={loading} />}

            {/* Phase 5 — Recent Closed Deals */}
            <ClosedDealsSection items={closedDeals} loading={loading} isAdmin={["master_admin","admin"].includes(user?.role)} />

            {/* Hot alerts + Pipeline */}
            <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
                <section className="mm-card p-5" data-testid="hot-panel">
                    <header className="mb-3 flex items-center justify-between">
                        <div>
                            <div className="mm-eyebrow flex items-center gap-1.5">
                                <Flame size={12} className="text-rose-600" />
                                Hot Lead Queue
                            </div>
                            <h2 className="mm-display mt-0.5 text-xl text-mm-navy">Triage board</h2>
                        </div>
                        <Link to="/admin/leads?hot=true" className="mm-btn-ghost">
                            All hot leads <ChevronRight size={14} />
                        </Link>
                    </header>
                    <div className="divide-y divide-mm-line">
                        {loading ? (
                            <div className="flex items-center gap-2 py-8 text-sm text-mm-muted">
                                <Loader2 size={14} className="animate-spin" /> Loading alerts…
                            </div>
                        ) : alerts.length === 0 ? (
                            <div className="py-8 text-sm text-mm-muted">
                                No open hot leads. Nice — stay sharp.
                            </div>
                        ) : (
                            alerts.map((a) => (
                                <div
                                    key={a.id}
                                    className="flex items-center justify-between gap-3 py-3"
                                    data-testid={`hot-alert-row-${a.id}`}
                                >
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate font-medium text-mm-ink">
                                                {a.name || "Unnamed"}
                                            </span>
                                            <span className="mm-chip !py-0 text-[10px]">
                                                {a.source}
                                            </span>
                                        </div>
                                        <div className="mt-0.5 text-xs text-mm-muted truncate">
                                            {a.product_interest || "—"} · score {a.score}/100
                                        </div>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                        <SlaTimer
                                            createdAt={a.created_at}
                                            acknowledgedAt={a.acknowledged_at || (a.acknowledged ? a.created_at : null)}
                                        />
                                        <Link
                                            to={a.lead_id ? `/admin/leads/${a.lead_id}/vip` : "/admin/leads"}
                                            className="mm-btn-outline !py-1.5 !px-3 text-[11px]"
                                            data-testid={`hot-alert-open-${a.id}`}
                                        >
                                            Open
                                        </Link>
                                        {!a.acknowledged && (
                                            <button
                                                onClick={() => handleAck(a.id)}
                                                className="mm-btn-primary !py-1.5 !px-3 text-[11px]"
                                                data-testid={`hot-alert-ack-${a.id}`}
                                            >
                                                Acknowledge
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </section>

                <section className="mm-card p-5" data-testid="pipeline-strip">
                    <div className="mm-eyebrow mb-3">Pipeline</div>
                    <h2 className="mm-display mb-4 text-xl text-mm-navy">By stage</h2>
                    <div className="space-y-1.5">
                        {(pipe?.stages || PIPELINE_STAGES.map((s) => ({ stage: s, count: 0 }))).map((s) => (
                            <Link
                                key={s.stage}
                                to={`/admin/leads?status=${encodeURIComponent(s.stage)}`}
                                className="flex items-center justify-between rounded-lg border border-mm-line px-3 py-2 text-sm transition-colors hover:bg-mm-bg"
                                data-testid={`pipeline-stage-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <span className="text-mm-ink">{s.stage}</span>
                                <span className="inline-flex items-center gap-2">
                                    <span className="text-mm-navy tabular-nums font-semibold">
                                        {s.count}
                                    </span>
                                    <ChevronRight size={14} className="text-mm-muted" />
                                </span>
                            </Link>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}

// -------------------- Phase 5 helpers --------------------

function centsToMoney(c) {
    if (c == null) return "$0";
    return `$${(Number(c) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function trendIcon(current, previous) {
    const a = Number(current) || 0;
    const b = Number(previous) || 0;
    if (a > b) return { Icon: ArrowUpRight, color: "text-emerald-700" };
    if (a < b) return { Icon: ArrowDownRight, color: "text-rose-700" };
    return { Icon: Minus, color: "text-mm-muted" };
}

function GoldKpiCard({ label, icon: Icon, value, subValue, prev, current, loading, testId }) {
    const { Icon: Trend, color } = trendIcon(current ?? value, prev);
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mm-card relative overflow-hidden p-5"
            style={{ background: `linear-gradient(135deg, ${GOLD}10, white 65%)`, borderColor: `${GOLD}66` }}
            data-testid={testId}
        >
            <div
                aria-hidden
                className="absolute -right-6 -top-6 h-20 w-20 rounded-full"
                style={{ background: GOLD, opacity: 0.18 }}
            />
            <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider" style={{ color: GOLD }}>
                <Icon size={13} />
                {label}
            </div>
            <div className="mt-2 mm-display text-3xl font-semibold text-mm-navy">
                {loading ? "—" : (value ?? 0)}
            </div>
            {subValue && (
                <div className="mt-0.5 text-xs text-mm-muted tabular-nums">{subValue}</div>
            )}
            <div className="mt-2 flex items-center gap-1 text-[11px]">
                <Trend size={12} className={color} />
                <span className={color}>vs last month</span>
            </div>
        </motion.div>
    );
}

function ClosedDealsSection({ items, loading, isAdmin }) {
    return (
        <section className="mm-card p-5" data-testid="dashboard-closed-deals">
            <header className="mb-3 flex items-center justify-between">
                <div>
                    <div className="mm-eyebrow" style={{ color: GOLD }}>
                        <DollarSign size={11} className="mr-1 inline" />
                        Recent Closed Deals
                    </div>
                    <h2 className="mt-0.5 mm-display text-lg text-mm-navy">
                        {isAdmin ? "Latest policies closed" : "Your latest closed policies"}
                    </h2>
                </div>
                <Link to="/admin/policies" className="mm-btn-ghost text-xs">
                    View all <ChevronRight size={12} />
                </Link>
            </header>
            {loading ? (
                <div className="py-8 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></div>
            ) : items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-mm-line bg-mm-bg/40 px-4 py-8 text-center text-sm text-mm-muted">
                    No closed deals this period.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                            <tr>
                                <th className="px-3 py-2 text-left font-medium">Lead</th>
                                {isAdmin && <th className="px-3 py-2 text-left font-medium">Agent</th>}
                                <th className="px-3 py-2 text-left font-medium">Carrier</th>
                                <th className="px-3 py-2 text-left font-medium">Type</th>
                                <th className="px-3 py-2 text-right font-medium">Coverage</th>
                                <th className="px-3 py-2 text-right font-medium">Monthly</th>
                                <th className="px-3 py-2 text-left font-medium">Effective</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((p) => (
                                <tr
                                    key={p.id}
                                    className="border-t border-mm-line"
                                    style={{ backgroundColor: `${GOLD}1A` }}
                                    data-testid={`dashboard-closed-deal-${p.id}`}
                                >
                                    <td className="px-3 py-2 font-medium text-mm-navy">{p.lead_name || "—"}</td>
                                    {isAdmin && <td className="px-3 py-2 text-mm-ink">{p.agent?.name || "—"}</td>}
                                    <td className="px-3 py-2">{p.carrier}</td>
                                    <td className="px-3 py-2">{p.policy_type}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{centsToMoney(p.coverage_amount)}</td>
                                    <td className="px-3 py-2 text-right font-semibold tabular-nums" style={{ color: GOLD }}>
                                        {centsToMoney(p.monthly_premium)}
                                    </td>
                                    <td className="px-3 py-2 text-mm-muted">{p.effective_date || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}


function PaymentWatchCard({ summary, loading }) {
    const stats = [
        {
            key: "reminders_upcoming_7d",
            label: "Reminders upcoming · 7d",
            icon: Bell,
            linkTo: "/admin/policies?payment_status=pending",
            accent: "text-sky-700 bg-sky-50 border-sky-200",
        },
        {
            key: "reminders_sent_7d",
            label: "Reminders sent · last 7d",
            icon: Mail,
            linkTo: "/admin/policies",
            accent: "text-emerald-700 bg-emerald-50 border-emerald-200",
        },
        {
            key: "open_alerts",
            label: "Open missed-draft alerts",
            icon: AlertCircle,
            linkTo: "/admin/policies?payment_status=missed",
            accent: "text-rose-700 bg-rose-50 border-rose-200",
        },
    ];
    return (
        <section className="mm-card p-5" data-testid="payment-watch-card">
            <header className="mb-3 flex items-center justify-between">
                <div>
                    <div className="mm-eyebrow flex items-center gap-1.5">
                        <CreditCardIcon /> Payment Watch
                    </div>
                    <h2 className="mm-display mt-0.5 text-xl text-mm-navy">Keep policies on book</h2>
                </div>
                <p className="max-w-xs text-[11px] italic text-mm-muted text-right">
                    Moving Mountains does not collect payments — we redirect clients to the carrier.
                </p>
            </header>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {stats.map((s) => {
                    const Icon = s.icon;
                    const val = summary?.[s.key] ?? 0;
                    return (
                        <Link
                            key={s.key}
                            to={s.linkTo}
                            className={`flex items-center justify-between rounded-lg border px-4 py-3 transition-colors hover:shadow-sm ${s.accent}`}
                            data-testid={`payment-watch-${s.key}`}
                        >
                            <div>
                                <div className="text-[10px] uppercase tracking-wider opacity-80">{s.label}</div>
                                <div className="mm-display mt-1 text-2xl font-semibold">
                                    {loading ? "—" : val}
                                </div>
                            </div>
                            <Icon size={20} className="opacity-70" />
                        </Link>
                    );
                })}
            </div>
        </section>
    );
}

function CreditCardIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
    );
}
